import {
  type FC, useEffect, useState,
} from 'react';
import { type Writable } from 'ts-essentials';
import { type TabsData } from '~/_shared/components/tabs/tabs.types';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useIsGeocodingRunningOrRequired } from '~/store/geocoding/geocoding.selectors';
import { useIsRowRelationsBeingProcessedOrRequiredSelector } from '~/store/rowRelations/rowRelations.selectors';
import { TabsComponent } from '../../_shared/components/tabs/tabs.component';
import { useTranslation } from '../../_shared/utils/hooks';
import { type BoundaryTerritoryGroup } from '../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type BoundaryGroup } from '../boundary.types';
import { BoundarySettingsComponent } from './common/boundarySettingsComponent';
import { BoundarySettingsModalComponent } from './modal/boundarySettingsModal.component';
import { BoundarySettingsSyncDataContainer } from './syncData/boundarySettingsSyncData.container';

type BoundarySettingsProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  boundaryGroup: BoundaryGroup;
  boundaryPermissions: BoundaryGroupPermissions;
  valueIsInitial: boolean;
  isLoading: boolean;
  isOpen: boolean;
  onBoundaryTerritoryGroupChange: (boundaryTerritoryGroup: BoundaryTerritoryGroup) => void;
  onClose: () => void;
  onSubmit: () => void;
  onBoundaryGroupNameChange?: (newName: string) => void;
}>;

export const BoundarySettingsModalDialogComponent: FC<BoundarySettingsProps> = (props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFormValidating, setIsFormValidating] = useState(false);
  const [isSyncFormFormValid, setIsSyncFormValid] = useState(true);
  const [syncSubmitDisabledReason, setSyncSubmitDisabledReason] = useState<string | null>(null);
  const isGeocodingRunningOrRequired = useIsGeocodingRunningOrRequired();
  const isRowRelationsBeingProcessedOrRequiredSelector = useIsRowRelationsBeingProcessedOrRequiredSelector();
  const [t] = useTranslation();

  const { boundaryIsCustom } = props.boundaryPermissions;

  const isGeocodingOrRowRelationSyncRequiredOrRunning = isGeocodingRunningOrRequired || isRowRelationsBeingProcessedOrRequiredSelector;

  useEffect(() => {
    if (isSyncFormFormValid) {
      if (isGeocodingOrRowRelationSyncRequiredOrRunning) {
        setSyncSubmitDisabledReason(t('labelValue.requiresGeocoding'));
        return;
      }
      setSyncSubmitDisabledReason(null);
    }
  }, [isFormValid, isGeocodingOrRowRelationSyncRequiredOrRunning, isSyncFormFormValid, t]);

  const tabsData: Writable<TabsData> = [{
    index: 0,
    header: boundaryIsCustom ? t('Territory Formatting').toUpperCase() : t('Boundary Formatting').toUpperCase(),
    child: (
      <BoundarySettingsComponent
        key={`boundary-settings-${props.boundaryTerritoryGroup.boundaryTerritoryGroupId}`} // drop component when group changes
        boundaryTerritoryGroup={props.boundaryTerritoryGroup}
        onBoundaryTerritoryGroupChange={props.onBoundaryTerritoryGroupChange}
        boundaryGroup={props.boundaryGroup}
        onBoundaryGroupNameChange={props.onBoundaryGroupNameChange}
        setIsFormValid={setIsFormValid}
        setIsLoading={setIsFormValidating}
      />
    ),
  }];

  if (props.boundaryGroup.matchings && (props.boundaryGroup.matchings.matchingColumns.length > 0)) {
    tabsData.push({
      index: 1,
      header: t('Sync Data').toUpperCase(),
      child: (
        <BoundarySettingsSyncDataContainer
          boundaryTerritoryGroup={props.boundaryTerritoryGroup}
          boundaryGroupMatchings={props.boundaryGroup.matchings}
          onBoundaryTerritoryGroupChange={props.onBoundaryTerritoryGroupChange}
          setIsFormValid={setIsSyncFormValid}
          setSubmitDisabledReason={setSyncSubmitDisabledReason}
        />
      ),
    });
  }

  return (
    <BoundarySettingsModalComponent
      isOpen={props.isOpen}
      onClose={props.onClose}
      isLoading={props.isLoading}
      isFormValidating={isFormValidating}
      isSubmitDisabled={!isSyncFormFormValid || !isFormValid || isGeocodingOrRowRelationSyncRequiredOrRunning}
      submitTooltipContent={syncSubmitDisabledReason}
      onSubmitClick={props.onSubmit}
      caption={boundaryIsCustom ? t('Territory Group Settings') : t('Boundary Settings')}
    >
      {(tabsData.length > 1) && (
        <TabsComponent
          activeTabIndex={activeTabIndex}
          data={tabsData}
          setActiveTabIndex={setActiveTabIndex}
        />
      )}
      {(tabsData.length === 1) && tabsData[0] && (
        tabsData[0].child
      )}
    </BoundarySettingsModalComponent>
  );
};
